import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ModalData, ModalRef } from '@purespectrum1/ui/modal';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { Subscription } from 'rxjs';
import { MultiBuyerSession } from '../../services/multi-buyer/multi-buyer-session';
import { AuthService } from '../../../auth/auth.service';
import { MultiBuyerCmp } from '../../interfaces/company.interface';
import { Router } from '@angular/router';

export const NO_COMPANY_REQUIRED_URLS = ['/dashboard'];

@Component({
  selector: 'ps-multi-buyer-modal',
  templateUrl: './multi-buyer-modal.component.html',
  styleUrls: ['./multi-buyer-modal.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class MultiBuyerModalComponent implements OnInit, OnDestroy {
  multiBuyersCmpsList: MultiBuyerCmp[] = [];
  selectedCompany?: MultiBuyerCmp;
  isMultiBuyerModal: boolean = true;
  isConfirmDisabled = true;
  isLoading = false;
  private _subscriptions: Subscription = new Subscription();

  constructor(
    @Inject(ModalData)
    private _modalData: {
      cmpId: number;
      buyerCompanyId: number;
    },
    private readonly _router: Router,
    readonly _modalRef: ModalRef<MultiBuyerModalComponent, string>,
    readonly _auth: AuthService,
    readonly _toastr: ToasterService,
    readonly _multiBuyerSession: MultiBuyerSession
  ) {}

  ngOnInit(): void {
    this._subscriptions.add(
      this._auth.multiBuyerModalCloseRequests$.subscribe(() => {
        this._modalRef.disableClose = false;
        this._modalRef.close();
      })
    );

    this._subscriptions.add(
      this._auth.selectedMultiBuyerCompany$.subscribe((company) => {
        this.selectedCompany = company;
        this._updateConfirmState();
      })
    );
    this.loadBuyers();
  }

  public get canClose(): boolean {
    return !!this._auth.selectedMultiBuyerCompany;
  }

  public async loadBuyers() {
    this.isLoading = true;
    try {
      this.multiBuyersCmpsList = await this._multiBuyerSession.buyers(
        this._modalData.cmpId,
        this._modalData.buyerCompanyId
      );
    } catch (error) {
      this._toastr.error('Failed to load buyer entities. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

  private _updateConfirmState(): void {
    const currentPath = this._router.url.split('?')[0];
    const isOptional = NO_COMPANY_REQUIRED_URLS.some(
      (route) => currentPath === route || currentPath.startsWith(route + '/')
    );
    this.isConfirmDisabled = isOptional ? false : !this.selectedCompany;
  }

  public chooseBuyer(company: MultiBuyerCmp): void {
    this.selectedCompany = company;
    this._updateConfirmState();
  }

  public clearBuyer() {
    this.selectedCompany = undefined;
    this._updateConfirmState();
  }

  public async confirmSelection() {
    this._modalRef.disableClose = false;
    if (!this.selectedCompany?.id) {
      this._auth.resetMultiBuyerToken();
      this._modalRef.disableClose = false;
      this._modalRef.close();
      return;
    }
    this.isLoading = true;
    await this._multiBuyerSession
      .selectBuyer(this._modalData.cmpId, this.selectedCompany.id)
      .catch((error) => {
        this._toastr.error(error);
      })
      .finally(() => {
        this.isLoading = false;
      });

    this._modalRef.close();
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }
}
